import React from 'react'
import { reduxForm, Field } from 'redux-form'
import uniq from 'lodash.uniq'

import classNames from './common.css'
import Modal from './Modal'
import ConfirmButtons from '../ConfirmButtons'
import LoadingSpinner from '../LoadingSpinner'
import GenericAutocomplete from '../GenericAutocomplete'
import KeywordList from '../KeywordList'
import KeywordGraphPicker from '../KeywordGraphPicker'
import { COLORS } from '../GroupedKeywords'
import { WEATHER_ARRAY } from '../../constants'

const INPUT_MAX_WIDTH = '280px'

function EditDynamicLink ({ handleSubmit, submitting, onCancel, title, types, keywordArray, useDates, toggleUseDates, useWeather, toggleUseWeather, getKeywordName, getKeywordGroup }) {
  const conditionType = useDates ? 'date' : 'text'
  return (
    <Modal title={title} onClose={onCancel}>
      <form onSubmit={handleSubmit} className={classNames.form} autoComplete='off'>
        <Field
          component={Input}
          props={{
            label: 'Table',
            component: 'select',
            className: classNames.weatherSelect
          }}
          type='text'
          name='type'
          required
        >
          {types.map(value => <option key={value} value={value}>{value}</option>)}
        </Field>
        <label className={classNames.noBottomMargin}>
          <input type='checkbox' checked={useDates} onChange={toggleUseDates} />
          Use a date condition
        </label>
        <label className={classNames.noTopMargin}>
          <input type='checkbox' checked={useWeather} onChange={toggleUseWeather} />
          Use a weather condition
        </label>
        {!useWeather &&
          <Field
            component={Input}
            props={{
              label: 'Condition' + (useDates ? ' (yyyy-mm-dd)' : ''),
              style: { maxWidth: (useDates ? '160px' : INPUT_MAX_WIDTH) }
            }}
            type={conditionType}
            name='condition'
            required
            placeholder={useDates ? 'yyyy-mm-dd' : 'Enter a condition'}
          />
        }
        {useWeather &&
          <Field
            component={Input}
            props={{
              component: 'select',
              label: 'Condition',
              className: classNames.weatherSelect,
              style: { maxWidth: INPUT_MAX_WIDTH }
            }}
            type={conditionType}
            name='condition'
            required
          >
            {WEATHER_ARRAY.map(value => <option key={value} value={value}>{value}</option>)}
          </Field>
        }
        <Field
          component={Input}
          props={{
            label: 'Keywords',
            component: KeywordPicker,
            keywordArray: keywordArray,
            getKeywordName,
            getKeywordGroup
          }}
          type='text'
          name='keywords'
          defaultValue={{
            search: '',
            keywords: []
          }}
        />
        <ConfirmButtons
          onCancel={onCancel}
          onConfirm={handleSubmit}
          confirmDisabled={submitting}
          confirmText={submitting ? 'Saving…' : 'Save'}
        />
        <LoadingSpinner show={submitting} />
      </form>
    </Modal>
  )
}

function Input ({ label, component, ...props }) {
  const Component = component || 'input'
  return (
    <div className={classNames.label}>
      {label}
      <Component {...props} />
      {props.touched && props.error &&
        <div className={classNames.error}>
          {props.error}
        </div>
      }
    </div>
  )
}

function KeywordPicker ({ value, onChange, keywordArray, getKeywordName, getKeywordGroup }) {
  const onChangeSearch = text => {
    onChange({
      ...value,
      search: text
    })
  }
  const onSelectKeyword = keyword => {
    onChange({
      search: '',
      keywords: value.keywords.concat(keyword.id)
    })
  }
  const onClickKeyword = (keyword, index) => {
    onChange({
      ...value,
      keywords: value.keywords.filter(id => getKeywordName(id) !== keyword)
    })
  }
  const onSelectGraphKeyword = node => {
    if (value.keywords.indexOf(node.id) === -1) {
      onChange({ ...value, keywords: value.keywords.concat(node.id) })
    } else {
      onChange({ ...value, keywords: value.keywords.filter(id => id !== node.id) })
    }
  }
  const onSelectAllChildren = nodes => {
    let allChildrenSelected = true
    for (let node of nodes) {
      if (value.keywords.indexOf(node.id) === -1) {
        allChildrenSelected = false
      }
    }
    if (allChildrenSelected) {
      onChange({
        ...value,
        keywords: value.keywords.filter(id => {
          return nodes.findIndex(n => n.id === id) === -1
        })
      })
    } else {
      onChange({
        ...value,
        keywords: uniq(value.keywords.concat(nodes.map(n => n.id)))
      })
    }
  }
  const keywordGroups = {}
  for (let id of value.keywords) {
    const group = getKeywordGroup(id)
    keywordGroups[group] = keywordGroups[group] || []
    keywordGroups[group].push(getKeywordName(id))
  }
  const keywords = Object.keys(keywordGroups).sort().map((key, index) => {
    const [r, g, b] = COLORS[index % COLORS.length]
    return <KeywordList keywords={keywordGroups[key]} onClickKeyword={onClickKeyword} r={r} g={g} b={b} />
  })
  return (
    <div>
      {value.keywords.length !== 0 &&
        <div className={classNames.note}>
          Click a keyword to remove
        </div>
      }
      {keywords}
      <GenericAutocomplete
        value={value.search}
        onChange={onChangeSearch}
        onSelect={onSelectKeyword}
        placeholder='Search for a keyword'
        suggestions={[
          {
            title: 'Keywords',
            items: keywordArray
          }
        ]}
        style={{ maxWidth: INPUT_MAX_WIDTH }}
      />
      <KeywordGraphPicker
        selected={value.keywords}
        onSelect={onSelectGraphKeyword}
        onSelectAllChildren={onSelectAllChildren}
      />
    </div>
  )
}

function validate (values, props) {
  const errors = {}
  if (!values.type) {
    errors.type = 'A table is required'
  }
  if (!values.condition) {
    errors.condition = 'A condition is required'
  }
  if (WEATHER_ARRAY.indexOf(values.condition) === -1 && /\d\d\d\d-\d\d-\d\d/.test(values.condition) === false) {
    errors.condition = 'Condition must be a date or weather type'
  }
  if (!values.keywords || values.keywords.keywords.length === 0) {
    errors.keywords = 'At least one keyword is required'
  }
  return errors
}

export default reduxForm({ form: 'dynlink', validate })(EditDynamicLink)



// WEBPACK FOOTER //
// ./src/components/modals/EditDynamicLink.js